import { AnimationWorkflow } from "@/lib/hooks/useAnimationWorkflow";

export const cardFlipWorkflow = (
  onFlipComplete?: () => void,
): AnimationWorkflow => ({
  id: "card-flip",
  name: "Simple Card Flip",
  steps: [
    {
      id: "flip-halfway",
      name: "Flip Halfway",
      duration: 1000,
      easing: "cubic-bezier(0.4, 0, 0.2, 1)",
      transform: {
        rotate: "90deg",
      },
      opacity: 0,
      onComplete: onFlipComplete,
    },
    {
      id: "flip-complete",
      name: "Complete Flip",
      duration: 1000,
      easing: "cubic-bezier(0.4, 0, 0.2, 1)",
      transform: {
        rotate: "180deg",
      },
      opacity: 1,
    },
  ],
});

export const shrinkFlipWorkflow = (
  shrinkDuration: number = 200,
  flipDuration: number = 2000,
): AnimationWorkflow => ({
  id: "shrink-flip",
  name: "Shrink Bounce + Vertical Flip",
  steps: [
    {
      id: "shrink-down",
      name: "Press Down",
      duration: shrinkDuration / 0.8,
      easing: "ease-out",
      transform: {
        scale: 0.95,
      },
    },
    {
      id: "flip-step",
      name: "Vertical Flip",
      duration: flipDuration,
      easing: "cubic-bezier(0.4, 0, 0.2, 1)",
      transform: {
        scale: 1.0,
        rotateY: "180deg",
      },
    },
  ],
});

export const flipAtCenterWorkflow = (
  duration: number = 2000,
): AnimationWorkflow => ({
  id: "flip-at-center",
  name: "180° Flip at Vertical Center",
  steps: [
    {
      id: "flip-to-center",
      name: "90° flip to edge-on view",
      duration: duration / 2,
      easing: "cubic-bezier(0.4, 0, 0.2, 1)",
      transform: {
        rotateY: "90deg",
      },
    },
    {
      id: "complete-flip",
      name: "180° flip showing back",
      duration: duration / 2,
      easing: "cubic-bezier(0.4, 0, 0.2, 1)",
      transform: {
        rotateY: "180deg",
      },
    },
  ],
});

export const cardRevealWorkflow = (
  onCardRevealed?: () => void,
  duration: number = 2000,
): AnimationWorkflow => ({
  id: "card-reveal",
  name: "Card Reveal (Back to Front)",
  steps: [
    {
      id: "shrink-down",
      name: "Press Down",
      duration: duration * 0.2,
      easing: "ease-out",
      transform: {
        scale: 0.95,
      },
    },
    {
      id: "turn-to-edge",
      name: "Turn 90° showing edge",
      duration: duration / 2,
      easing: "cubic-bezier(0.4, 0, 0.2, 1)",
      transform: {
        rotateY: "90deg",
        scale: 0.9,
      },
      onComplete: onCardRevealed,
    },
    {
      id: "reveal-front",
      name: "Reveal front face",
      duration: duration / 2,
      easing: "cubic-bezier(0.4, 0, 0.2, 1)",
      transform: {
        rotateY: "0deg",
        scale: 0.9,
      },
    },
  ],
});

// Predefined animation workflows for common patterns
export const cardDrawWorkflow = (
  _onCardRevealed?: () => void,
  onAnimationComplete?: () => void,
): AnimationWorkflow => ({
  id: "card-draw",
  name: "Card Drawing Animation",
  steps: [
    ...shrinkFlipWorkflow().steps,
    {
      id: "move-to-position",
      name: "Move to Final Position",
      duration: 1000,
      easing: "cubic-bezier(0.4, 0, 0.2, 1)",
      transform: {
        translateX: "-40vw",
        translateY: "-20vh",
        scale: 0.4,
        rotateY: "180deg",
      },
    },
    {
      id: "fade-out-card",
      name: "Fade Out Moving Card",
      duration: 200,
      opacity: 0,
      transform: {
        scale: 1.0,
        rotateY: "180deg",
      },
    },
  ],
  onWorkflowComplete: onAnimationComplete,
});

export const centerToPositionWorkflow = (
  duration: number = 1500,
): AnimationWorkflow => ({
  id: "center-to-position",
  name: "Move from center to final position",
  steps: [
    {
      id: "start-at-center",
      name: "Start at center position",
      duration: 0,
      transform: {
        translateX: "calc(50vw - 50%)",
        translateY: "calc(25vh - 50%)",
      },
    },
    {
      id: "move-to-final",
      name: "Move to final position",
      duration,
      easing: "cubic-bezier(0.4, 0, 0.2, 1)",
      transform: {
        translateX: "0",
        translateY: "0",
      },
    },
  ],
});

// Helper function to create custom workflows easily
export const createCustomWorkflow = (
  id: string,
  name: string,
  steps: Parameters<typeof cardDrawWorkflow>[0] extends any[] ? never : any[],
  onComplete?: () => void,
): AnimationWorkflow => ({
  id,
  name,
  steps,
  onWorkflowComplete: onComplete,
});
